import { useEffect } from 'react';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function About() {
  useEffect(() => {
    document.title = '关于我们 - 欲火视频';
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', window.location.origin + '/about');
  }, []);

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于欲火视频',
    description: '了解欲火视频的品牌故事、发展历程、口碑评价及联系方式。欲火视频致力于为用户提供最优质的在线影视观看体验。',
    url: window.location.origin + '/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '欲火视频',
      url: window.location.origin,
    },
    about: {
      '@type': 'Organization',
      name: '欲火视频',
      description: '欲火视频是专业的在线视频平台，提供海量高清影视资源、热门剧集、电影大片及短视频内容。',
      foundingDate: '2020',
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '400-888-8888',
        contactType: 'customer service',
        availableLanguage: 'Chinese',
      },
      address: {
        '@type': 'PostalAddress',
        streetAddress: '影视产业园区88号',
        addressLocality: '北京',
        addressRegion: '朝阳区',
        addressCountry: 'CN',
      },
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: window.location.origin + '/' },
      { '@type': 'ListItem', position: 2, name: '关于我们' },
    ],
  };

  return (
    <main>
      <Breadcrumb items={[{ name: '关于我们' }]} />

      <section className="w-full px-4 md:px-6 max-w-5xl mx-auto pb-16">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary-100 text-primary-700 text-sm font-medium mb-4">
            <i className="ri-fire-line"></i>
            <span>关于欲火视频</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold font-heading text-foreground-950 mb-4">
            欲火视频 — 让每一帧都精彩
          </h1>
          <p className="text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
            欲火视频创立于2020年，是国内领先的在线视频平台。我们致力于为用户提供海量高清影视资源，涵盖电影、电视剧、动漫、综艺、纪录片等多元内容，让每一位用户都能在这里找到心仪的影视作品。
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {[
            { icon: 'ri-eye-line', title: '海量资源', desc: '拥有超过10万部影视作品，涵盖各类题材和类型，满足不同用户的多样化观影需求。' },
            { icon: 'ri-shield-check-line', title: '高清画质', desc: '采用先进的视频编码技术，提供从标清到超高清的全画质选择，确保最佳观看体验。' },
            { icon: 'ri-smartphone-line', title: '多终端适配', desc: '完美适配电脑、平板和手机等多种设备，随时随地畅享欲火视频的精彩内容。' },
          ].map((item, index) => (
            <div key={index} className="p-6 rounded-xl bg-background-50 border border-background-200 text-center">
              <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                <i className={`${item.icon} text-xl`}></i>
              </div>
              <h3 className="text-lg font-semibold text-foreground-900 mb-2">{item.title}</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>

        <div className="mb-16">
          <h2 className="text-2xl font-bold font-heading text-foreground-950 text-center mb-10">
            品牌故事
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20creative%20office%20space%20with%20large%20screens%20displaying%20video%20content%2C%20young%20professionals%20collaborating%2C%20warm%20lighting%2C%20tech%20company%20vibe%2C%20contemporary%20interior%20design%2C%20clean%20aesthetic&width=800&height=500&seq=yuhuo-about-story&orientation=landscape"
                alt="欲火视频团队工作场景"
                className="w-full rounded-xl object-cover"
                title="欲火视频 - 专业在线视频平台"
              />
            </div>
            <div className="space-y-4">
              <p className="text-base text-foreground-700 leading-relaxed">
                欲火视频的创立源于创始团队对影视文化的热爱。我们注意到，尽管互联网上充斥着各种视频内容，但用户依然面临着内容分散、画质参差不齐、更新不及时等问题。
              </p>
              <p className="text-base text-foreground-700 leading-relaxed">
                于是，欲火视频应运而生。我们汇聚了一支由影视行业资深人士和技术精英组成的团队，致力于打造一个内容全面、体验流畅、更新及时的在线视频平台。
              </p>
              <p className="text-base text-foreground-700 leading-relaxed">
                经过数年的发展，欲火视频已经从一个小型视频站点成长为拥有超过5000万活跃用户的综合性视频平台。我们始终坚持以用户为中心，不断优化产品体验，丰富内容库，让欲火视频成为用户观看影视内容的首选平台。
              </p>
            </div>
          </div>
        </div>

        <div className="mb-16">
          <h2 className="text-2xl font-bold font-heading text-foreground-950 text-center mb-10">
            用户口碑
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                name: '张先生',
                role: '资深影迷',
                content: '欲火视频是我用过最好的在线视频平台。内容更新非常及时，最新的电影和剧集都能第一时间看到。画质清晰，播放流畅，强烈推荐！',
                rating: 5,
              },
              {
                name: '李女士',
                role: '追剧达人',
                content: '作为一个重度追剧爱好者，欲火视频完全满足了我的需求。分类清晰，搜索方便，而且手机端体验也很棒，随时随地都能追剧。',
                rating: 5,
              },
              {
                name: '王同学',
                role: '大学生',
                content: '课余时间最喜欢在欲火视频上看纪录片和动漫。内容丰富，而且完全免费，对于我们学生来说真的太友好了。欲火视频，值得信赖！',
                rating: 5,
              },
            ].map((review, index) => (
              <div key={index} className="p-6 rounded-xl bg-background-50 border border-background-200">
                <div className="flex items-center gap-1 mb-3">
                  {Array.from({ length: review.rating }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                  {review.content}
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-bold text-sm">
                    {review.name[0]}
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground-900">{review.name}</div>
                    <div className="text-xs text-foreground-500">{review.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div id="contact" className="mb-4">
          <h2 className="text-2xl font-bold font-heading text-foreground-950 text-center mb-10">
            联系我们
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 flex-shrink-0">
                  <i className="ri-map-pin-line text-lg"></i>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-foreground-900 mb-1">
                    <a href="#contact" className="hover:text-primary-500 transition-colors">公司地址</a>
                  </h4>
                  <p className="text-sm text-foreground-600">北京市朝阳区影视产业园区88号</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 flex-shrink-0">
                  <i className="ri-mail-line text-lg"></i>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h4>
                  <p className="text-sm text-foreground-600">contact@yuhuo.com</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 flex-shrink-0">
                  <i className="ri-phone-line text-lg"></i>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-foreground-900 mb-1">客服热线</h4>
                  <p className="text-sm text-foreground-600">400-888-8888</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 flex-shrink-0">
                  <i className="ri-time-line text-lg"></i>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-foreground-900 mb-1">工作时间</h4>
                  <p className="text-sm text-foreground-600">周一至周日 9:00 - 21:00</p>
                </div>
              </div>
            </div>

            <div className="rounded-xl bg-background-100 border border-background-200 p-6">
              <h3 className="text-lg font-semibold text-foreground-900 mb-4">发送消息</h3>
              <form className="space-y-4">
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                    placeholder="请输入您的邮箱"
                  />
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                  <textarea
                    id="contact-message"
                    name="message"
                    rows={4}
                    maxLength={500}
                    required
                    className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                    placeholder="请输入您的留言内容（最多500字）"
                  ></textarea>
                </div>
                <button
                  type="submit"
                  className="w-full py-2.5 rounded-lg bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  发送消息
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
    </main>
  );
}